// Senha por aba (texto 10, D): envolve o conteúdo de uma aba. Com "Pedir senha ao abrir esta aba" ligado
// (Ajustes > Abas > [aba]), o conteúdo NÃO é montado enquanto a aba estiver trancada (nem por um instante):
// no lugar aparece o mesmo desbloqueio do app (auth/LockScreen: senha, PIN, desenho ou biometria).
// Pede uma vez ao entrar; dentro da aba não pede de novo; de novo só depois de sair, conforme a regra (lib/travaAba).
// Na primeira vez em Consultório, Saúde ou Documentos, sugere ligar ("Quer proteger esta aba com senha?").
import { useEffect, useRef, useSyncExternalStore, type ReactNode } from 'react';
import { Alert, View } from 'react-native';
import { useIsFocused } from 'expo-router';
import * as Auth from '../auth/AuthContext';
import LockScreen from '../auth/LockScreen';
import { useDoc } from '../store/store';
import { aba, C, useCores } from './theme';
import { t } from '../lib/i18n';
import type { AbaId } from '../store/types';
import {
  ABAS_SEM_TRAVA, ABAS_SUGERIR_SENHA, abaAberta, abaTrancouEm, assinarTravaAba, desbloquearAba, entrarNaAba,
  marcarSugestaoSenha, mudarSenhaAba, sairDaAba, versaoTravaAba,
} from '../lib/travaAba';

const LOGO_DEPOIS = 60000;   // desbloqueou o app há menos de 1 minuto (depois de a aba fechar): não pede outra senha
// no navegador o AuthContext.web não tem esta função (lá não há bloqueio do app)
const ultimoDesbloqueio = (): number => { try { return typeof Auth.ultimoDesbloqueio === 'function' ? Auth.ultimoDesbloqueio() : 0; } catch { return 0; } };

let sugestaoAberta = false;

export function TravaAba({ id, onBack, children }: { id: AbaId; onBack: () => void; children: ReactNode }) {
  const aj = useDoc('ajustes');
  const regra = ABAS_SEM_TRAVA.includes(id) ? null : (aj?.senhaAba?.[id] ?? null);
  useSyncExternalStore(assinarTravaAba, versaoTravaAba);
  const foco = useIsFocused();
  const { status } = Auth.useAuth();
  const { corDe } = useCores();
  const appAberto = status === 'unlocked';
  // o app acabou de ser desbloqueado nesta renderização (o AuthContext só marca a hora depois, num efeito)
  const statusAntes = useRef(status);
  const appAcabouDeAbrir = appAberto && statusAntes.current !== 'unlocked';
  useEffect(() => { statusAntes.current = status; }, [status]);

  // entrar/sair da aba (trocar de aba, ir para Ajustes...). O app em segundo plano é tratado em lib/travaAba (aoVoltar).
  useEffect(() => {
    if (!regra || !foco) return;
    entrarNaAba(id);
    return () => sairDaAba(id);
  }, [id, foco, !!regra]); // eslint-disable-line react-hooks/exhaustive-deps

  const trancada = !!regra && !abaAberta(id);
  // acabou de desbloquear o app (depois de a aba fechar): é o mesmo desbloqueio, não pede duas senhas seguidas
  const ult = appAcabouDeAbrir ? Date.now() : ultimoDesbloqueio();
  const fechou = abaTrancouEm(id);
  const pular = trancada && appAberto && foco && fechou > 0 && ult >= fechou && Date.now() - ult < LOGO_DEPOIS;
  useEffect(() => { if (pular) desbloquearAba(id); }, [pular, id]);

  // primeira vez em Consultório, Saúde ou Documentos: sugere ligar (uma vez só)
  const sugerir = !regra && foco && appAberto && ABAS_SUGERIR_SENHA.includes(id) && !!aj && !aj.senhaAbaSugerida?.includes(id);
  useEffect(() => {
    if (!sugerir || sugestaoAberta) return;
    sugestaoAberta = true;
    const i = setTimeout(() => {
      marcarSugestaoSenha(id);
      Alert.alert(t('Quer proteger esta aba com senha?'),
        t('Ao abrir esta aba, o app pede o mesmo desbloqueio do app (senha, biometria ou PIN). Dá para mudar em Ajustes > Abas.'), [
          { text: t('Agora não'), style: 'cancel', onPress: () => { sugestaoAberta = false; } },
          { text: t('Ligar'), onPress: () => { sugestaoAberta = false; mudarSenhaAba(id, '5min', true); } },
        ], { cancelable: true, onDismiss: () => { sugestaoAberta = false; } });
    }, 700);
    return () => { clearTimeout(i); sugestaoAberta = false; };
  }, [sugerir, id]);

  if (!trancada) return <>{children}</>;
  // app bloqueado (a tela de bloqueio do app está por cima) ou abrindo sozinha agora: só o fundo, sem o conteúdo
  if (!appAberto || pular || !foco) return <View style={{ flex: 1, backgroundColor: C.bg }} />;
  return (
    <View style={{ flex: 1, backgroundColor: C.bg }}>
      <LockScreen key={id} cor={corDe(id)} titulo={t('{nome} protegida com senha', { nome: aba(id).nome })}
        onOk={() => desbloquearAba(id)} onVoltar={onBack} />
    </View>
  );
}
